import React, { useEffect, useState } from "react";
import { Modal, Button, List, Tag, Space, notification, Spin } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SwapOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Api from "api";
import styled from "styled-components";
import { ThemeConfig } from "theme";

const StyledList = styled(List)`
  .ant-list-item {
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
  }
  .change-old-name {
    text-decoration: line-through;
    color: #999;
    margin-right: 8px;
  }
  .change-new-name {
    color: ${ThemeConfig.colors.primary};
    font-weight: 600;
  }
  .change-authority {
    color: #888;
    font-size: 12px;
  }
`;

interface Props {
  isOpen: boolean;
  onClose: (action?: string) => void;
  collectionId: string;
}

const changeTypeColors: Record<string, string> = {
  rename: "blue",
  reclassify: "purple",
  remove: "red",
  merge: "orange",
  split: "cyan",
};

const TaxonomicChangesPopup = ({ isOpen, onClose, collectionId }: Props) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [changes, setChanges] = useState<any[]>([]);
  const [processing, setProcessing] = useState<Record<string, boolean>>({});

  const fetchChanges = async () => {
    setLoading(true);
    try {
      const { data } = await Api.get(
        `/collections/${collectionId}/taxonomic-changes`,
        { status: "pending" }
      );
      if (data.success) {
        setChanges(data.data);
      }
    } catch (e: any) {
      console.error("Failed to fetch taxonomic changes", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      fetchChanges();
    }
  }, [isOpen]);

  const handleAccept = async (changeId: string) => {
    setProcessing((prev) => ({ ...prev, [changeId]: true }));
    try {
      const { data } = await Api.post(
        `/collections/${collectionId}/taxonomic-changes/${changeId}/accept`,
        {}
      );
      if (data.success) {
        notification.success({
          message: "Change Accepted",
          description: data.messages?.[0] || "Species reference updated.",
        });
        setChanges((prev) => prev.filter((c) => c._id !== changeId));
      }
    } catch (e: any) {
      notification.error({
        message: "Failed",
        description:
          e?.response?.data?.messages?.[0] || "Failed to accept change",
      });
    }
    setProcessing((prev) => ({ ...prev, [changeId]: false }));
  };

  const handleReject = async (changeId: string) => {
    setProcessing((prev) => ({ ...prev, [changeId]: true }));
    try {
      const { data } = await Api.post(
        `/collections/${collectionId}/taxonomic-changes/${changeId}/reject`,
        {}
      );
      if (data.success) {
        notification.success({
          message: "Change Rejected",
          description: data.messages?.[0] || "Original name kept.",
        });
        setChanges((prev) => prev.filter((c) => c._id !== changeId));
      }
    } catch (e: any) {
      notification.error({
        message: "Failed",
        description:
          e?.response?.data?.messages?.[0] || "Failed to reject change",
      });
    }
    setProcessing((prev) => ({ ...prev, [changeId]: false }));
  };

  const handleAcceptAll = async () => {
    const items = changes.map((c) => ({
      changeId: c._id,
      action: "accept",
    }));
    setLoading(true);
    try {
      const { data } = await Api.post(
        `/collections/${collectionId}/taxonomic-changes/bulk-resolve`,
        { items }
      );
      if (data.success) {
        notification.success({
          message: "All Changes Accepted",
          description: data.messages?.[0],
        });
        setChanges([]);
      }
    } catch (e: any) {
      notification.error({
        message: "Bulk Accept Failed",
        description:
          e?.response?.data?.messages?.[0] || "Failed to process bulk accept",
      });
    }
    setLoading(false);
  };

  const remainingCount = changes.length;

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: "#faad14" }} />
          <span>Taxonomic Changes Detected</span>
        </Space>
      }
      open={isOpen}
      onCancel={() => onClose(remainingCount === 0 ? "refetch" : undefined)}
      width={700}
      footer={[
        <Button
          key="later"
          onClick={() =>
            onClose(remainingCount === 0 ? "refetch" : undefined)
          }
        >
          Review Later
        </Button>,
        remainingCount > 0 && (
          <Button
            key="acceptAll"
            type="primary"
            onClick={handleAcceptAll}
            loading={loading}
          >
            Accept All ({remainingCount})
          </Button>
        ),
      ].filter(Boolean)}
    >
      {loading && changes.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : changes.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <CheckCircleOutlined
            style={{ fontSize: 48, color: "#52c41a", marginBottom: 16 }}
          />
          <p>All taxonomic changes have been resolved!</p>
        </div>
      ) : (
        <>
          <p style={{ marginBottom: 16, color: "#666" }}>
            {remainingCount} species in this collection have taxonomic updates.
            Please review each change:
          </p>
          <StyledList
            style={{ maxHeight: 400, overflow: "auto" }}
            dataSource={changes}
            renderItem={(change: any) => (
              <List.Item
                actions={[
                  <Button
                    key="accept"
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    loading={processing[change._id]}
                    onClick={() => handleAccept(change._id)}
                  >
                    Accept
                  </Button>,
                  <Button
                    key="reject"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    loading={processing[change._id]}
                    onClick={() => handleReject(change._id)}
                  >
                    Reject
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space size="small" wrap>
                      <Tag color={changeTypeColors[change.change_type] || "default"}>
                        {change.change_type}
                      </Tag>
                      <span className="change-old-name">{change.old_name}</span>
                      {change.new_name && (
                        <>
                          <SwapOutlined />
                          <span className="change-new-name">
                            {change.new_name}
                          </span>
                        </>
                      )}
                    </Space>
                  }
                  description={
                    <span className="change-authority">
                      per {change.authority}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        </>
      )}
    </Modal>
  );
};

export default TaxonomicChangesPopup;
